import React from "react";
import { Eye, Globe2, MousePointerClick, UsersRound } from "lucide-react";

const SOURCE_LABELS = {
  direct: "Accesso diretto",
  google: "Google",
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X / Twitter",
  youtube: "YouTube",
  other: "Altri siti",
};

function MetricCard({ icon: Icon, label, value, detail }) {
  return (
    <article className="card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-cream p-2"><Icon size={20} className="text-primary" /></span>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold text-muted">{label}</p>
          <strong className="block text-2xl sm:text-3xl mt-1">{Number(value || 0).toLocaleString("it-IT")}</strong>
          {detail && <p className="text-xs text-muted mt-1">{detail}</p>}
        </div>
      </div>
    </article>
  );
}

export function AdminSiteStats({ data }) {
  if (!data) return null;
  const today = data.today || {};
  const week = data.last7Days || {};
  const total = data.sinceTracking || {};
  const startedAt = data.startedAt ? new Date(data.startedAt).toLocaleString("it-IT") : "—";

  return (
    <section className="mt-8 border-t border-edge pt-6">
      <div className="flex items-start gap-3 mb-4">
        <Globe2 className="text-primary shrink-0" size={22} />
        <div>
          <h2 className="text-xl">Sito youvoice.social</h2>
          <p className="text-xs text-muted">Statistiche separate dalla web app. Nessun IP, cookie pubblicitario o identificatore personale salvato. Raccolta avviata: {startedAt}.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={UsersRound} label="Sessioni oggi" value={today.sessions} detail={`${Number(week.sessions || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
        <MetricCard icon={Eye} label="Pagine viste oggi" value={today.pageViews} detail={`${Number(week.pageViews || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
        <MetricCard icon={MousePointerClick} label="Clic verso la web app" value={today.webappClicks} detail={`${Number(week.webappClicks || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
        <MetricCard icon={MousePointerClick} label="Conversione sito → app" value={`${Number(data?.conversion?.today || 0).toLocaleString("it-IT")}%`} detail={`${Number(data?.conversion?.last7Days || 0).toLocaleString("it-IT")}% negli ultimi 7 giorni`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <section className="card p-4 sm:p-5">
          <h3 className="text-lg">Pagine più visitate</h3>
          <div className="mt-4 space-y-2">
            {(data.topPages || []).length === 0 && <p className="text-sm text-muted">Nessuna visita registrata.</p>}
            {(data.topPages || []).map((item) => (
              <div key={item.page} className="flex items-center justify-between gap-3 rounded-xl border border-edge px-3 py-2">
                <span className="text-sm truncate">{item.page}</span>
                <strong>{Number(item.count || 0).toLocaleString("it-IT")}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="card p-4 sm:p-5">
          <h3 className="text-lg">Provenienza delle sessioni</h3>
          <div className="mt-4 space-y-2">
            {(data.sources || []).length === 0 && <p className="text-sm text-muted">Nessuna sessione registrata.</p>}
            {(data.sources || []).map((item) => (
              <div key={item.source} className="flex items-center justify-between gap-3 rounded-xl border border-edge px-3 py-2">
                <span className="text-sm">{SOURCE_LABELS[item.source] || item.source}</span>
                <strong>{Number(item.count || 0).toLocaleString("it-IT")}</strong>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="card p-4 sm:p-5 mt-4 overflow-x-auto">
        <h3 className="text-lg">Andamento sito — ultimi 14 giorni</h3>
        <table className="w-full mt-3 text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="py-2 pr-3">Giorno</th>
              <th className="py-2 px-2">Sessioni</th>
              <th className="py-2 px-2">Pagine</th>
              <th className="py-2 pl-2">Clic web app</th>
            </tr>
          </thead>
          <tbody>
            {(data.daily || []).map((row) => (
              <tr key={row.day} className="border-t border-edge">
                <td className="py-2 pr-3 whitespace-nowrap">{new Date(`${row.day}T12:00:00Z`).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</td>
                <td className="py-2 px-2">{row.sessions}</td>
                <td className="py-2 px-2">{row.pageViews}</td>
                <td className="py-2 pl-2">{row.webappClicks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card p-4 sm:p-5 mt-4">
        <h3 className="text-lg">Totali dal tracciamento del sito</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <div className="rounded-xl border border-edge p-3"><span className="block text-xs text-muted">Sessioni</span><strong className="text-lg">{Number(total.sessions || 0).toLocaleString("it-IT")}</strong></div>
          <div className="rounded-xl border border-edge p-3"><span className="block text-xs text-muted">Pagine viste</span><strong className="text-lg">{Number(total.pageViews || 0).toLocaleString("it-IT")}</strong></div>
          <div className="rounded-xl border border-edge p-3"><span className="block text-xs text-muted">Clic web app</span><strong className="text-lg">{Number(total.webappClicks || 0).toLocaleString("it-IT")}</strong></div>
          <div className="rounded-xl border border-edge p-3"><span className="block text-xs text-muted">Conversione</span><strong className="text-lg">{Number(data?.conversion?.sinceTracking || 0).toLocaleString("it-IT")}%</strong></div>
        </div>
      </section>
    </section>
  );
}
